import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import ReactFlow, { Background, Controls, MiniMap, Handle, Position, BackgroundVariant, useReactFlow } from "reactflow";
import "reactflow/dist/style.css";
import "./FlowChart.css";
import dagre from "dagre";
import { toPng } from 'html-to-image';
import { useQuery } from "@tanstack/react-query";
import { hierarchyQuery } from "../../api";
import { LoadingOverlay } from "../../common/loaders/LoadingOverlay";
import { S3_IMAGES_URL } from "../../common/utils";
import {
  collectAllExpandable,
  collectExpandedToDepth,
  resolveNodeDisplay,
  buildNodeTooltip,
  preprocessChart,
} from './flowChartUtils';
import { TemplateProfileImage, SimplePeopleIcon, SimpleSearchIcon } from "../../common/icons";
import { useTranslation } from "react-i18next";
import Tooltip from '@mui/material/Tooltip';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 100;

/** Applies dagre layout to flat React Flow nodes/edges and returns them with positions */
const getLayoutedElements = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 90 });

  nodes.forEach((n) => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  return {
    nodes: nodes.map((n) => {
      const pos = g.node(n.id);
      return { ...n, position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 } };
    }),
    edges
  };
}

/**
 * Builds positioned React Flow nodes and edges from the nested chart tree.
 * Only nodes in `expanded` have their children traversed.
 */
const buildGraph = (chartData, expanded) => {
  const nodes = [];
  const edges = [];

  const traverse = (node, parentNr = null) => {
    const isExpanded = expanded.has(node.nr);
    const hasChildren = node.children?.length > 0;

    nodes.push({
      id: String(node.nr),
      type: "orgNode",
      data: { name: node.name, jobTitle: node.jobTitle || "", hasChildren, isExpanded, childCount: node.children?.length ?? 0, employmentEnded: node.employmentEnded ?? false },
      position: { x: 0, y: 0 }
    });

    if (parentNr !== null) {
      edges.push({
        id: `e-${parentNr}-${node.nr}`,
        source: String(parentNr),
        target: String(node.nr),
        type: "smoothstep",
        style: { stroke: "#0B0B14", strokeWidth: 0.5 }
      });
    }

    if (isExpanded) {
      node.children?.forEach((child) => traverse(child, node.nr));
    }
  }

  (Array.isArray(chartData) ? chartData : [chartData]).forEach((root) => traverse(root, null));
  return getLayoutedElements(nodes, edges);
}

/** Exposes the ReactFlow instance via `apiRef` for use outside the ReactFlow provider */
const FlowExporter = ({ apiRef }) => {
  const rf = useReactFlow();
  apiRef.current = rf;
  return null;
}

/**
 * Custom React Flow node rendering a person card.
 * Visual props are injected via `data` by the nodeTypes factory to keep nodeTypes referentially stable.
 */
 const OrgNode = ({ data, id }) => {
  const { name, jobTitle, hasChildren, isExpanded, childCount, onToggle, photo, eSalaryCompanyId, isDimmed, teamColor, tooltipTitle, employmentEnded } = data;

  let photoUrl = null;
  if (photo && eSalaryCompanyId) {
    if (photo.includes('.')) {
      photoUrl = `https://phantasos.heeros.com:8025/images/${eSalaryCompanyId}/${photo}`;
    } else {
      photoUrl = S3_IMAGES_URL + photo;
    }
  }

  const handleKeyDown = (e) => {
    if (hasChildren && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggle(id);
    }
  };

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ top: -22, visibility: 'hidden' }} />
      <Tooltip title={tooltipTitle || ''} placement="top" arrow enterDelay={700} disableInteractive>
      <div
        onClick={() => hasChildren && onToggle(id)}
        onKeyDown={handleKeyDown}
        role={hasChildren ? 'button' : undefined}
        tabIndex={hasChildren ? 0 : undefined}
        className={['org-node-card', hasChildren && 'expandable', isDimmed && 'dimmed', employmentEnded && 'ended'].filter(Boolean).join(' ')}
        style={teamColor ? { borderTop: `8px solid ${teamColor}` } : { borderTop: '8px solid #5952e0' }}
      >
        <div className="org-node-inner">
          <div className="org-node-content">
            <div className="org-node-name">{name}</div>
            {jobTitle && <div className="org-node-job-title">{jobTitle}</div>}
          </div>
        </div>
        <div className="org-node-avatar">
          {photoUrl
            ? <img src={photoUrl} crossOrigin="anonymous" alt={name} />
            : <TemplateProfileImage />
          }
        </div>
        {hasChildren && (
          <div className="org-node-pill">
            <SimplePeopleIcon />
            <span>{childCount}</span>
            <span className="org-node-pill-arrow">{isExpanded ? '▲' : '▼'}</span>
          </div>
        )}
      </div>
      </Tooltip>
      <Handle type="source" position={Position.Bottom} style={{ bottom: -6, visibility: 'hidden' }} />
    </>
  );
}

/**
 * Interactive org chart powered by React Flow + dagre.
 * - Depth toolbar to expand to level 1/2/3, all, or collapse
 * - Click nodes to toggle subtrees; search auto-expands ancestor paths
 * - Team filter chips highlight members by team
 */
const OrgChart = ({ photoMap = {}, eSalaryCompanyId, workTitleMap = {}, teams = [] }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(new Set());
  const [activeDepth, setActiveDepth] = useState(2);
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);
  const rootRef = useRef(null);
  const flowApiRef = useRef(null);
  const [elements, setElements] = useState({ nodes: [], edges: [] });
  const { data, isLoading, isError } = useQuery(hierarchyQuery);

  // Preprocess the chart data to handle ended employments before any memoized calculations
  const mappedData = useMemo(() => {
    if (!data?.chart) return null;
    const roots = Array.isArray(data.chart) ? data.chart : [data.chart];
    return roots.map(preprocessChart).filter(Boolean);
  }, [data]);

  const parentMap = useMemo(() => {
    if (!mappedData) return {};
    const map = {};
    const traverse = (node, parentNr = null) => {
      if (parentNr !== null) map[node.nr] = parentNr;
      node.children?.forEach(child => traverse(child, node.nr));
    }
    mappedData.forEach(root => traverse(root, null));
    return map;
  }, [mappedData]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    if (!value) {
      setDebouncedQuery('');
    } else {
      debounceRef.current = setTimeout(() => setDebouncedQuery(value), 300);
    }
  }, []);

  const searchMatches = useMemo(() => {
    if (!debouncedQuery.trim() || !mappedData) return new Set();
    const q = debouncedQuery.toLowerCase();
    const matches = new Set();
    const traverse = (node) => {
      if (
        node.name?.toLowerCase().includes(q) ||
        (workTitleMap[node.nr] ?? node.jobTitle ?? '').toLowerCase().includes(q)
      ) matches.add(node.nr);
      node.children?.forEach(traverse);
    }
    mappedData.forEach(traverse);
    return matches;
  }, [debouncedQuery, mappedData, workTitleMap]);

  const expandToDepth = useCallback((depth) => {
    if (!mappedData) return;
    if (depth === 'all') {
      setExpanded(collectAllExpandable(mappedData));
    } else if (depth === 0) {
      setExpanded(new Set());
    } else {
      setExpanded(collectExpandedToDepth(mappedData, depth));
    }
    setActiveDepth(depth);
  }, [mappedData]);

  const toggleNode = useCallback((id) => {
    setActiveDepth(null); // manual interaction -> no preset active
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(Number(id))) {
        next.delete(Number(id));
      } else {
        next.add(Number(id));
      }
      return next;
    });
  }, []);

  // Clean up debounce timer on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Export the chart as a PNG image. Uses instant fitView (duration: 0) then captures with html-to-image.
  const handleExport = useCallback(async () => {
    if (flowApiRef.current?.fitView) {
      flowApiRef.current.fitView({ padding: 0.15, duration: 0 });
      await new Promise(r => setTimeout(r, 50));
    }
    const flowEl = rootRef.current?.querySelector('.react-flow__renderer');
    if (!flowEl) return;
    try {
      const dataUrl = await toPng(flowEl, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
        skipFonts: true,
      });
      const link = document.createElement('a');
      link.download = 'org-chart.png';
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Chart export failed:', err);
    }
  }, []);

  const nodeTypes = useMemo(() => {
    const OrgNodeRenderer = (props) => {
      const nr = Number(props.id);
      const title = workTitleMap[nr] ?? props.data.jobTitle;
      const { isDimmed, nodeTeamColor } = resolveNodeDisplay(
        nr, debouncedQuery, searchMatches, activeFilter, teams
      );
      const tooltipTitle = buildNodeTooltip(
        props.data.name,
        title,
        props.data.hasChildren,
        props.data.isExpanded,
        t,
        props.data.employmentEnded
      )

      return (
        <OrgNode
          {...props}
          data={{
            ...props.data,
            onToggle: toggleNode,
            photo: photoMap[nr],
            eSalaryCompanyId,
            jobTitle: title,
            isDimmed,
            teamColor: nodeTeamColor,
            tooltipTitle,
            employmentEnded: props.data.employmentEnded,
          }}
        />
      );
    }
    return { orgNode: OrgNodeRenderer };
  }, [t, toggleNode, photoMap, eSalaryCompanyId, workTitleMap, teams, activeFilter, debouncedQuery, searchMatches]);

  useEffect(() => {
    if (mappedData?.length) {
      setExpanded(collectExpandedToDepth(mappedData, 2));
      setActiveDepth(2);
    }
  }, [mappedData]);

  useEffect(() => {
    if (!mappedData) return;
    const { nodes, edges } = buildGraph(mappedData, expanded);
    setElements({ nodes, edges });
  }, [mappedData, expanded]);

  // Expand ancestor path for every search match so matches are always visible
  useEffect(() => {
    if (!searchMatches.size) return;
    setActiveDepth(null);
    setExpanded(prev => {
      const next = new Set(prev);
      searchMatches.forEach(nr => {
        let cur = parentMap[nr];
        while (cur != null) { next.add(cur); cur = parentMap[cur]; }
      });
      return next;
    });
  }, [searchMatches, parentMap]);

  if (isError) return <div className="fc-error">{t('ErrorFetchingData')}</div>;

  return (
    <div className="fc-root" ref={rootRef}>
      <LoadingOverlay isVisible={isLoading} />

      {/* team filter chips */}
      {teams.length > 0 && (
        <div className="fc-filter-chips">
          {teams.map((team) => {
            const isActive = activeFilter === team.teamCode;
            return (
              <Tooltip
                key={team.teamCode}
                title={isActive
                  ? t('FilterChipClear')
                  : t('FilterChipApply')}
                arrow
                placement="top"
              >
                <button
                  onClick={() => setActiveFilter(isActive ? null : team.teamCode)}
                  className={`fc-filter-chip${isActive ? ' active' : ''}`}
                  style={isActive ? { borderColor: team.color, transition: 'border-color 0.2s ease', color: team.color, background: '#F3F3F7' } : {}}
                >
                  <span className="fc-filter-chip-dot" style={{ background: team.color }} />
                  {team.teamName}
                </button>
              </Tooltip>
            );
          })}
          {activeFilter && (
            <Tooltip title={t('ClearFilterTooltip')} arrow placement="top">
              <button
                aria-label={t('ClearFilter')}
                onClick={() => setActiveFilter(null)}
                className="fc-filter-chip-clear"
              >
                ✕ {t('ClearFilter')}
              </button>
            </Tooltip>
          )}
        </div>
      )}

      {/* search box */}
      <div className="fc-search-wrapper">
        <div className={`fc-search-box${searchQuery ? ' active' : ''}`}>
          <SimpleSearchIcon className="fc-search-icon" />
          <input
            type="text"
            aria-label={t('SearchPeople')}
            placeholder={t('SearchPeople') + '...'}
            value={searchQuery}
            onChange={handleSearchChange}
            className="fc-search-input"
          />
          {searchQuery && (
            <Tooltip title={t('ClearSearch')} arrow placement="top">
              <button
                aria-label={t('ClearSearch')}
                onClick={() => { setSearchQuery(''); setDebouncedQuery(''); clearTimeout(debounceRef.current); }}
                className="fc-search-clear"
              >✕</button>
            </Tooltip>
          )}
        </div>
        {searchQuery.trim() && (
          <div className={`fc-search-count${searchMatches.size ? ' has-results' : ''}`}>
            {searchMatches.size
              ? `${t('SearchResultCount')} ${searchMatches.size}`
              : t('SearchNoResults')}
          </div>
        )}
      </div>

      {/* depth toolbar */}
      {data?.chart && (
        <div className="fc-depth-toolbar">
          <span className="fc-depth-label">{t('Depth')}</span>
          <div className="fc-divider" />
          {[1, 2, 3].map((level) => (
            <Tooltip
              key={level}
              title={t('ExpandToLevel' + level)}
              arrow
              placement="top"
            >
              <button
                onClick={() => expandToDepth(level)}
                className={`fc-seg-btn${activeDepth === level ? ' active' : ''}`}
              >
                {level}
              </button>
            </Tooltip>
          ))}
          <Tooltip title={t('ExpandAllTooltip')} arrow placement="top">
            <button
              onClick={() => expandToDepth('all')}
              className={`fc-seg-btn${activeDepth === 'all' ? ' active' : ''}`}
            >
              {t('All')}
            </button>
          </Tooltip>
          <div className="fc-divider" />
          <Tooltip title={t('CollapseAllTooltip')} arrow placement="top">
            <button
              onClick={() => expandToDepth(0)}
              className={`fc-seg-btn collapse${activeDepth === 0 ? ' active' : ''}`}
            >
              {t('Collapse')}
            </button>
          </Tooltip>
          <Tooltip title={t('ExportChartTooltip')} arrow placement="top">
            <button onClick={handleExport} className="fc-seg-btn">
              {t('Export')}
            </button>
          </Tooltip>
        </div>
      )}

      <ReactFlow
        nodes={elements.nodes}
        edges={elements.edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.15}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} />
        <Controls />
        <MiniMap nodeStrokeWidth={3} zoomable pannable />
        <FlowExporter apiRef={flowApiRef} />
      </ReactFlow>
    </div>
  );
}

export default OrgChart;
